<template>
    <div data-component="OrderDetailsDetailsCol"
         class="details-col">
        <div class="details-col__details">
            <!-- Order reference -->
            <div class="larger-detail hidden-xs">
                <i class="fa-solid fa-barcode"></i>
                <h6>{{ l('ORDER_DETAILS.BOOKING_REF') }}</h6>
                <p>{{ order.bookingRef }}</p>
            </div>

            <!-- Staff reference -->
            <div v-if="order.staffRef">
                <i class="fa-solid fa-tag"></i>
                <h6>{{ l('ORDER_DETAILS.STAFF_REF') }}</h6>
                <p>{{ order.staffRef }}</p>
            </div>

            <!-- Greek Txn Number -->
            <div v-if="greekTxnNumber">
                <i class="fa-solid fa-hashtag"></i>
                <h6>{{ l('ORDER_DETAILS.TRANSACTION_NUMBER') }}</h6>
                <p>{{ greekTxnNumber }}</p>
            </div>

            <!-- Order Collection Number -->
            <div v-if="orderDisplayCollectionNumber">
                <i class="fa-solid fa-hand-holding-box"></i>
                <h6>{{ l('ORDER_DETAILS.ORDER_COLLECTION_NUMBER') }}</h6>
                <p>{{ orderDisplayCollectionNumber }}</p>
            </div>

            <!-- Order total -->
            <div>
                <i class="fa-solid fa-coins"></i>
                <h6>{{ l('ORDER_DETAILS.TOTAL') }}</h6>
                <p>
                    <a v-if="order.payments.length > 0"
                       class="btn btn-default"
                       @click.prevent="showPayments()">
                        {{ l('ORDER_DETAILS.PAYMENTS') }}
                    </a>
                    {{ currencyFilter(order.getTotal()) }}
                </p>
            </div>

            <!-- Payment status -->
            <div>
                <i v-if="!order.isConfirmed() || order.hasPendingPayments || order.partiallyPaid || order.overpaid"
                   class="fa-solid fa-exclamation-circle"></i>
                <i v-else-if="order.fullyPaid"
                   class="fa-solid fa-check-circle"></i>
                <i v-else
                   class="fa-solid fa-times-circle"></i>

                <h6>{{ l('ORDER_DETAILS.PAYMENT_STATUS') }}</h6>

                <p v-if="order.hasPendingPayments"
                   class="text-warning">
                    <!-- There is a pending payment. But we show a button in case the user wants to (over) pay now. -->
                    <PayBalanceButton v-if="order.isLive()"
                                      :order="order"
                                      :is-refund="false"
                                      @click="payBalance(false)" />
                    {{ l('ORDER_PAYMENT_STATUS.PENDING') }}
                </p>
                <p v-else-if="order.overpaid"
                   class="text-warning">
                    <!-- Too much has been paid for the order. Show a button to refund the difference. -->
                    <!-- Shows for cancelled orders too because refunding those is important functionality. -->
                    <PayBalanceButton v-if="order.isConfirmed()"
                                      :order="order"
                                      :is-refund="true"
                                      @click="payBalance(true)" />
                    {{ l('ORDER_PAYMENT_STATUS.OVERPAID') }}
                </p>
                <p v-else-if="order.fullyPaid"
                   class="text-success">
                    {{ l('ORDER_PAYMENT_STATUS.FULLY_PAID') }}
                </p>
                <p v-else-if="order.partiallyPaid"
                   class="text-warning">
                    <!-- Not enough has been paid for the order. Show a button to pay the remaining balance. -->
                    <PayBalanceButton v-if="order.isLive()"
                                      :order="order"
                                      :is-refund="false"
                                      @click="payBalance(false)" />
                    {{ l('ORDER_PAYMENT_STATUS.PART_PAID') }}
                </p>
                <p v-else-if="order.unpaid"
                   class="text-danger">
                    <!-- Nothing has been paid for the order. Show a button to pay the remaining balance. -->
                    <PayBalanceButton v-if="order.isLive()"
                                      :order="order"
                                      :is-refund="false"
                                      @click="payBalance(false)" />
                    {{ l('ORDER_PAYMENT_STATUS.UNPAID') }}
                </p>

                <p v-if="showPendingGoCardlessMessage"
                   class="text-sm clear italic text-lightly-muted">
                    {{ l('ORDER_DETAILS.PENDING_GOCARDLESS_INFO') }}
                </p>
            </div>

            <!-- Order Status (shown for cancelled or unconfirmed orders) -->
            <div v-if="!order.isLive()">
                <i class="fa-solid fa-exclamation-triangle"></i>
                <h6>{{ l('ORDER_DETAILS.ORDER_STATUS') }}</h6>
                <!-- Voided status (unconfirmed AND cancelled) -->
                <p v-if="order.isVoided()"
                   class="text-danger">
                    {{ l('ORDER_STATUS.VOIDED') }}
                    <br />{{ dateFilter(order.cancelledAt, true) }}
                </p>
                <!-- Cancelled status -->
                <p v-else-if="order.isCancelled()"
                   class="text-danger">
                    {{ l('ORDER_STATUS.CANCELLED') }}
                    <br />{{ dateFilter(order.cancelledAt, true) }}
                </p>
                <!-- Unconfirmed status -->
                <p v-else-if="!order.isConfirmed()"
                   class="text-danger">
                    {{ l('ORDER_STATUS.UNCONFIRMED') }}
                </p>
            </div>
            <div v-else-if="order.redeemed !== null">
                <!-- Ticket Status (shown for confirmed and not-cancelled orders) -->
                <!-- When an offline redemption happens order.redeemed becomes null. We then hide this
                to prevent showing a redemption status that we can no longer be sure is correct. -->
                <i class="fa-solid fa-ticket-simple"></i>
                <h6>{{ l('ORDER_DETAILS.TICKET_STATUS') }}</h6>
                <p v-if="order.redemptionDate"
                   class="text-danger">
                    {{ l('ORDER_DETAILS.TICKET_STATUS_REDEEMED') }}
                    <br />{{ dateFilter(order.redemptionDate, true) }}
                </p>
                <p v-else-if="hasIncompleteWaivers"
                   class="text-warning">
                    {{ l('ORDER_DETAILS.TICKET_STATUS_OUTSTANDING_WAIVERS') }}
                </p>
                <p v-else
                   class="text-success">
                    {{ l('ORDER_DETAILS.TICKET_STATUS_REDEEMABLE') }}
                </p>
            </div>

            <!-- Group Booking -->
            <div v-if="manageGroupBookings">
                <i class="fa-solid fa-user-group"></i>
                <h6>{{ l('ORDER_DETAILS.GROUP_BOOKING') }}</h6>
                <p>{{ order.groupBooking ? l('YES') : l('NO') }}</p>
            </div>

            <!-- Order date -->
            <div>
                <i class="fa-solid fa-calendar"></i>
                <h6>{{ l('ORDER_DETAILS.ORDER_DATE') }}</h6>
                <p>{{ dateFilter(order.date, true) }}</p>
            </div>

            <!-- Branch name (for cross-branch orders) -->
            <div v-if="showBranchNames">
                <i class="fa-solid fa-shop"></i>
                <h6>{{ l('ORDER_DETAILS.ORDER_BRANCH') }}</h6>
                <p> {{ order.orderBranch.name }}</p>
            </div>

            <!-- Payment channel -->
            <div>
                <i class="fa-solid fa-shopping-cart"></i>
                <h6>{{ l('ORDER_DETAILS.PAYMENT_CHANNEL') }}</h6>
                <p>{{ order.paymentChannel.name }}</p>
            </div>

            <!-- Notes -->
            <div v-if="order.notes">
                <i class="fa-solid fa-comments"></i>
                <h6>{{ l('ORDER_DETAILS.NOTES') }}</h6>
                <p>{{ order.notes }}</p>
            </div>

            <!-- Customer organisation -->
            <div v-if="order.customer.account && order.customer.account.organisation">
                <i class="fa-solid fa-building"></i>
                <h6>{{ l('CUSTOMER_DETAILS.ORGANISATION') }}</h6>
                <p>{{ order.customer.account.organisation }}</p>
            </div>

            <!-- Customer name -->
            <div v-if="customerName">
                <i class="fa-solid fa-user"></i>
                <h6>{{ l('CUSTOMER_DETAILS.NAME') }}</h6>
                <p>{{ customerName }}</p>
            </div>

            <!-- Customer telephone -->
            <div v-if="customerTel">
                <i class="fa-solid fa-phone"></i>
                <h6>{{ l('CUSTOMER_DETAILS.TELEPHONE') }}</h6>
                <p>{{ customerTel }}</p>
            </div>

            <!-- Customer email -->
            <div v-if="customerEmail">
                <i class="fa-solid fa-envelope"></i>
                <h6>{{ l('CUSTOMER_DETAILS.EMAIL') }}</h6>
                <p>{{ customerEmail }}</p>
            </div>

            <!-- Custromer address -->
            <div v-if="customerAddress">
                <i class="fa-solid fa-house"></i>
                <h6>{{ l('CUSTOMER_DETAILS.ADDRESS') }}</h6>
                <p>{{ customerAddress }}</p>
            </div>

            <!-- Order level custom fields (from new separate order level/item level groups) -->
            <div v-for="fieldInstance in orderLevelFieldInstancesToShow"
                 :key="fieldInstance.key"
                 class="detail order-level-field-instance">
                <i class="fa-solid fa-info-circle"></i>
                <h6>{{ fieldInstance.field.name }}</h6>
                <p>
                    <FieldInstanceValue :instance="fieldInstance" />
                </p>
            </div>
        </div>
    </div>
</template>

<script>

import DocumentTypeEnum from '@/lib/Orders/DocumentNumbers/DocumentTypeEnum';
import EventNames from '@/lib/Events/EventNames';
import FieldInstanceValue from '@/vue/components/CustomFields/FieldInstanceValue';
import PayBalanceButton from '@/epos/vue/pages/order-details/components/PayBalanceButton';
import PaymentMethodRef from '@/lib/PaymentMethods/PaymentMethodRef';
import { addressFilter } from '@/vue/filters/addressFilter';
import { contactNameFilter } from '@/vue/filters/contactNameFilter';
import { container } from '@/container';
import { getDocumentNumberFromOrder } from '@/lib/Orders/DocumentNumbers';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';

export default {
    components: { PayBalanceButton, FieldInstanceValue },

    props: {
        /**
         * @type {Order}
         */
        order: {
            type: Object,
            required: true,
        },

        isCrossBranch: {
            type: Boolean,
            default: false,
        },

        hasIncompleteWaivers: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        showBranchNames() {
            return this.isCrossBranch;
        },

        /**
         * @returns {FieldInstance[]}
         */
        orderLevelFieldInstancesToShow() {
            return this.order.orderLevelFieldGroup?.instances.filter((instance) => instance.showValueToStaff()) || [];
        },

        /**
         * @returns {?Contact}
         */
        customerContactWithName() {
            return this.order.customer.getContactContainingName();
        },

        /**
         * @returns {string}
         */
        customerName() {
            return this.customerContactWithName ? contactNameFilter(this.customerContactWithName) : '';
        },

        customerTel() {
            return this.order.customer.getTel();
        },

        customerEmail() {
            return this.order.customer.getEmail();
        },

        customerAddress() {
            const address = this.order.customer.getAddress();
            if (address && !address.isEmpty()) {
                return addressFilter(address);
            }

            return '';
        },

        /**
         * Greek tax sequential transaction number from an annually-resetting sequence.
         * There are separate sequences per sales channel (ProPoint and Customer Facing). So the same number can
         * exist in both sequences. Prior to PROP-2961 we just showed the number, which could have been misleading.
         * So it is now prefixed with "PP-" or "CF-" as appropriate to differentiate between the sequences.
         *
         * @return {string|null}
         */
        greekTxnNumber() {
            // Lists of document types to look for, in order of preference.
            const documentTypes = [
                {
                    type: DocumentTypeEnum.GREEK_TXN_NUMBER_PROPOINT,
                    prefix: 'PP-',
                },
                {
                    type: DocumentTypeEnum.GREEK_TXN_NUMBER_CUSTOMER_FACING,
                    prefix: 'CF-',
                },
            ];

            for (const {
                type,
                prefix,
            } of documentTypes) {
                const docNumber = getDocumentNumberFromOrder(this.order, type);
                if (docNumber?.displayNumber) {
                    return `${prefix}${docNumber.displayNumber}`;
                }
            }

            return null;
        },

        orderDisplayCollectionNumber() {
            return this.order.displayCollectionNumber;
        },

        manageGroupBookings() {
            return this.$store.state.currentDevice.manageGroupBookings;
        },

        showPendingGoCardlessMessage() {
            return this.order.payments.some(
                /**
                 * @param {Payment} p
                 */
                (p) => p.pending && !p.paid && p.paymentMethod.ref === PaymentMethodRef.GOCARDLESS,
            );
        },
    },

    methods: {
        showPayments() {
            container.vueModalFactory.openAndIgnoreResult(
                MODAL_NAMES__EPOS.ORDER_PAYMENTS,
                {
                    payments: this.order.payments,
                    orderTotal: this.order.getTotal(),
                }
            );
        },

        /**
         * @param {boolean} isRefund
         */
        payBalance(isRefund) {
            // TODO: Extract the logic for editing an order to a service and call that service here,
            // instead of the Angular controller being in charge.
            container.eventBus.broadcast(
                EventNames.PAY_ORDER_BALANCE_REQUESTED,
                {
                    isRefund,
                }
            );
        },
    },
};
</script>
